import React, { forwardRef, useEffect, useState } from 'react';
import { Input, Radio, Space, type InputProps, type InputRef, type Rule } from 'antd';
import useIsNHS from '@hooks/useIsNHS';
import useMetaMisc from '@hooks/useMetaMisc';
import { validate as validateNHS } from 'nhs-number-validator';
import { useTranslation } from 'react-i18next';
import { RegistrationFormItem, type RegistrationFormItemProps } from '../RegistrationFormItem';
import { useRegistrationFormInstance } from '../useSelfRegistrationFormInstance';

export const MembershipNumberFormItem = forwardRef<InputRef, RegistrationFormItemProps & { inputProps?: InputProps }>(
  ({ inputProps, ...props }, ref) => {
    const isNHS = useIsNHS();
    const form = useRegistrationFormInstance();

    const { t } = useTranslation('common');

    const { rules, maxLength, optional } = useMembershipNumberRules();
    const hasMembershipNumber = !!form.getFieldValue('mem_no');

    const [membershipNumberKnown, setMembershipNumberKnown] = useState<boolean>(hasMembershipNumber);

    const [storedMembershipNumber, setStoredMembershipNumber] = useState<string | undefined>(
      form.getFieldValue('mem_no')
    );

    return (
      <>
        {optional && (
          <RegistrationFormItem
            label={isNHS ? t('Do you know your NHS number?') : t('Do you know your AXA Health membership number?')}
            hasFeedback
            required
            validateTrigger="onBlur"
            rules={[{ required: true }]}
          >
            <Radio.Group
              value={membershipNumberKnown}
              onChange={({ target: { value } }) => {
                setMembershipNumberKnown(value);

                if (!value) {
                  setStoredMembershipNumber(form.getFieldValue('mem_no'));
                  form.setFieldsValue({ mem_no: undefined });
                } else {
                  form.setFieldsValue({ mem_no: storedMembershipNumber });
                }
              }}
              disabled={inputProps?.disabled}
            >
              <Space direction="vertical">
                <Radio value>{t('Yes')}</Radio>
                <Radio value={false}>{t('No')}</Radio>
              </Space>
            </Radio.Group>
          </RegistrationFormItem>
        )}

        {(membershipNumberKnown || !optional) && (
          <RegistrationFormItem
            label={t('MembershipNumber', { membershipOrNHS: isNHS ? t('NHS') : t('AXA Health Membership') })}
            name="mem_no"
            hasFeedback
            validateTrigger="onBlur"
            rules={rules}
            required
            {...props}
          >
            <Input maxLength={maxLength} data-testid="nhs-or-membership-number" ref={ref} {...inputProps} />
          </RegistrationFormItem>
        )}
      </>
    );
  }
);

function useMembershipNumberRules() {
  const isNHS = useIsNHS();
  const { field_validators } = useMetaMisc();
  const [rules, setRules] = useState<Rule[]>([{ required: true }]);
  const fieldValidator = field_validators?.find(({ rule_code }) => rule_code === 'MEMBERSHIP_NO');

  useEffect(() => {
    if (fieldValidator?.nhs_number_flag) {
      setRules([getNhsNumberRule(fieldValidator.error_text, isNHS)]);
    } else if (fieldValidator?.regex) {
      setRules([createRuleFromFieldValidator(fieldValidator)]);
    } else if (typeof field_validators === 'object') {
      setRules([{ required: true }]);
    }
  }, [fieldValidator, field_validators, isNHS]);

  return { rules, maxLength: fieldValidator?.field_max_length, optional: !isNHS };
}

function createRuleFromFieldValidator({
  field_max_length,
  regex,
  sample_data
}: NonNullable<ReturnType<typeof useMetaMisc>['field_validators']>[0]): Rule {
  const pattern = regex ? new RegExp(regex) : undefined;

  const message = sample_data
    ? `Membership number has to be in the format ${sample_data}`
    : 'Membership number is invalid';

  return {
    pattern,
    max: field_max_length,
    validator: (_, value) => {
      if (!value) {
        return Promise.reject(Error('Membership number is required'));
      }

      if (pattern && !pattern.test(value)) {
        return Promise.reject(Error(message));
      }

      return Promise.resolve();
    }
  };
}

function getNhsNumberRule(errorText: string, isNHS: boolean): Rule {
  return {
    validator: (_, value) => (!isNHS || validateNHS(value) ? Promise.resolve() : Promise.reject(Error(errorText)))
  };
}
